import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Theme, MenuItem, Typography, IconButton, Menu, Badge, Grid } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { PurchaseTypes, Shopper } from 'common/types';
import ShopperDetails from '../ShopperDetails';
import ShopperReturnInformation from '../ShopperReturnInformation';
import * as ViewSelectors from 'selectors/ViewSelectors';
import {
	getDurationString,
	getTotalRentalDurationFromProducts,
	getEarliestProductStartTime,
	getLatestProductEndTime,
} from 'common/utils/dateCalculations';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import classNames from 'classnames';
import { RiPrinterLine, RiPercentLine, RiChat3Line } from 'react-icons/ri';
import { getDiscountForShopper } from 'common/utils/pricing';
import * as RentalSelectors from 'selectors/RentalViewSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import ReturnTime from '../ReturnTime';
import EditView from './EditView';
import { useTranslation } from 'services/localization/useTranslation';
import { usePermissions } from 'hooks/usePermissions';

interface Props {
	shopper: Shopper;
	shopperIndex: number;
	editingShopper: boolean;
	editingPrice: boolean;
	openDiscountDialog: () => void;
	printReceipt: (id: string) => void;
	triggerDeleteShopper: () => void;
	openAdditionalInformationDialog: () => void;
	setEditingShopper: (isEditing: boolean) => void;
}

const ShopperInfo = (props: Props) => {
	const {
		shopper,
		shopperIndex,
		editingShopper,
		printReceipt,
		openDiscountDialog,
		triggerDeleteShopper,
		openAdditionalInformationDialog,
		setEditingShopper,
		editingPrice,
	} = props;
	const classes = useStyles();
	const { hasPermission } = usePermissions();
	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const order = useSelector(RentalSelectors.activeOrder)!;
	const { rentalInfo, shoppers, products } = order;
	const shopperId = shopper.id;
	const shopperProducts = products.filter((p) => p.shopperId === shopperId);
	const shopperHasRentals = shopperProducts.some((p) => p.purchaseType === PurchaseTypes.rental);

	const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);

	const { t } = useTranslation();
	const activeState = rentalInfo.activeState;
	const anyDetailsToRender =
		shopper.segment || Object.keys(shopper.userProperties || {}).length ? true : false;
	const rentalDuration = getTotalRentalDurationFromProducts(shopperProducts);

	const language = useSelector(ViewSelectors.lang);

	const renderShopperName = () => {
		const shopperFullName = shopper.firstName + ' ' + shopper.lastName;
		return shopper.firstName || shopper.lastName ? (
			<div className={classes.nameContainer}>
				<Typography className={classNames(classes.flexGrow, classes.shopperName)} variant="h5">
					{shopperFullName}
				</Typography>
				{shopperHasRentals && (
					<ShopperReturnInformation
						rentalInfo={rentalInfo}
						shopper={shopper}
						shopperProducts={shopperProducts}
						stockProducts={stockProducts}
						rental={order}
					/>
				)}
			</div>
		) : (
			<div className={classes.nameContainer}>
				<Typography
					className={classNames(classes.flexGrow, classes.shopperName)}
					variant="h5"
					color="textSecondary"
				>
					{t('common:person.person')} {(shopperIndex + 1).toString()}
				</Typography>
				{shopperHasRentals && (
					<ShopperReturnInformation
						rentalInfo={rentalInfo}
						shopper={shopper}
						shopperProducts={shopperProducts}
						stockProducts={stockProducts}
						rental={order}
					/>
				)}
			</div>
		);
	};

	const handleMoreOptionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setMoreAnchorEl(event.currentTarget);
	};

	const renderRentalPeriodInfo = () => {
		const shopperStartDate = getEarliestProductStartTime(shopperProducts);
		const shopperEndDate = getLatestProductEndTime(shopperProducts) || rentalInfo.endDateReturned;
		return (
			<Grid
				container
				spacing={1}
				alignItems={'center'}
				className={classes.rentalPeriodInfoContainer}
			>
				<Grid item>{renderDurationInformation()}</Grid>
				<Grid item>
					{rentalDuration && rentalDuration.durationInSeconds !== 0 ? (
						<ReturnTime
							shopperStartDate={shopperStartDate}
							shopperEndDate={shopperEndDate}
							productsReturned={Boolean(shopper.productsReturned)}
						/>
					) : null}
				</Grid>
			</Grid>
		);
	};

	const renderShopperDetails = () => {
		return <ShopperDetails shopper={shopper} activeState={activeState} />;
	};

	const renderDurationInformation = () => {
		return (
			<Grid container className={classes.durationInformationContainer}>
				<Grid item xs={'auto'}>
					<Typography variant="body2" id="duration-string">
						{getDurationString(rentalDuration, 'long', language, t)}
					</Typography>
				</Grid>
			</Grid>
		);
	};

	return (
		<div className={classes.shopperDetailsContainer}>
			{editingShopper ? (
				<EditView shopper={shopper} endEditing={() => setEditingShopper(false)} />
			) : (
				<React.Fragment>
					<Grid container wrap="nowrap" justifyContent="space-between">
						<Grid item>{renderShopperName()}</Grid>
						<Grid item>
							<IconButton
								disabled={!!shopper.productsReturned || editingPrice}
								color={shopper.productsReturned ? 'default' : 'primary'}
								classes={{ root: classes.moreBtn }}
								onClick={handleMoreOptionsClick}
								aria-controls="more-options"
								aria-haspopup="true"
								size="large"
							>
								<MoreVertIcon />
							</IconButton>
							<Menu
								id="more-options"
								anchorEl={moreAnchorEl}
								open={Boolean(moreAnchorEl)}
								onClose={() => setMoreAnchorEl(null)}
								keepMounted
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'right',
								}}
								transformOrigin={{
									vertical: 'top',
									horizontal: 'right',
								}}
							>
								<MenuItem
									disabled={!hasPermission('edit_booking')}
									onClick={() => {
										setEditingShopper(true);
										setMoreAnchorEl(null);
									}}
								>
									{t('common:actions.edit')}
								</MenuItem>
								<MenuItem
									disabled={
										rentalInfo.rentalState === 'ACTIVE' ||
										rentalInfo.rentalState === 'COMPLETED' ||
										shoppers.length === 1 ||
										!hasPermission('edit_booking')
									}
									onClick={() => {
										triggerDeleteShopper();
										setMoreAnchorEl(null);
									}}
								>
									{t('common:actions.delete')}
								</MenuItem>
							</Menu>
						</Grid>
					</Grid>
					<Grid container>
						{shopperHasRentals && (
							<Grid item xs={12}>
								{renderRentalPeriodInfo()}
							</Grid>
						)}
						{anyDetailsToRender && (
							<Grid item xs={12} className={classes.shopperDetailElement}>
								{renderShopperDetails()}
							</Grid>
						)}
					</Grid>
					<IconButton
						onClick={() => {
							printReceipt(shopper.id);
						}}
						size="large"
					>
						<RiPrinterLine />
					</IconButton>
					<IconButton
						onClick={() => {
							openDiscountDialog();
						}}
						size="large"
						disabled={!hasPermission('edit_booking')}
					>
						<Badge
							invisible={!getDiscountForShopper(shopperProducts, products, shopper, order)}
							variant="dot"
							overlap="circular"
							color="primary"
							classes={{
								badge: classes.discountBadge,
								anchorOriginTopRightCircular: classes.dotBadgeStyles,
							}}
						>
							<RiPercentLine />
						</Badge>
					</IconButton>
					<IconButton
						aria-label="edit-shopper-additional-information"
						onClick={() => openAdditionalInformationDialog()}
						size="large"
					>
						<Badge
							invisible={!shopper.additionalInformation}
							variant="dot"
							overlap="circular"
							color="primary"
							classes={{
								badge: classes.discountBadge,
								anchorOriginTopRightCircular: classes.dotBadgeStyles,
							}}
						>
							<RiChat3Line />
						</Badge>
					</IconButton>
				</React.Fragment>
			)}
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		shopperDetailsContainer: {
			height: '100%',
			[theme.breakpoints.up('md')]: {
				borderRight: 'solid 1px' + theme.palette.divider,
				paddingRight: theme.spacing(3),
			},

			[theme.breakpoints.down('md')]: {
				borderBottom: 'solid 1px' + theme.palette.divider,
				marginBottom: theme.spacing(2),
			},
		},
		shopperDetailElement: {
			marginBottom: '28px',
		},
		shopperName: {
			marginRight: '16px',
			fontWeight: 500,
		},
		close: {
			padding: theme.spacing(0.5),
			position: 'absolute',
			top: '14px',
			right: '14px',
		},
		rentalPeriodInfoContainer: {
			marginBottom: '16px',
		},
		durationInformationContainer: {
			minWidth: '115px',
		},
		flexGrow: {
			flexGrow: 1,
		},
		nameContainer: {
			display: 'flex',
			flexWrap: 'wrap',
			marginBottom: theme.spacing(3),
			marginTop: theme.spacing(1),
		},
		moreBtn: {
			padding: 0,
			minWidth: theme.spacing(6),
			minHeight: theme.spacing(6),
		},
		spacingBottom: {
			paddingBottom: theme.spacing(2),
		},
		discountBadge: {
			height: 10,
			minWidth: 10,
			backgroundColor: theme.palette.success.main,
		},
		dotBadgeStyles: {
			top: '7%',
			right: '7%',
		},
	}),
);

export default ShopperInfo;
